import { useEffect, useState } from 'react';
import { useGameStore } from '@/state/gameStore';
import { getDailyQuest } from '@/domain/dailyQuests';
import { Icon, type IconName } from '@/ui/components/Icon';
import './DailyQuestPanel.css';

/**
 * Compact daily-quest card for the Hero tab. Shows the player's current
 * quest with progress bar + claim button. Countdown ticks every second
 * so the player sees the "Reset in 03:42:17" feel without ad-hoc rAF.
 *
 * Reset itself is handled in App.tsx (refreshDailyQuest tick every 60 s);
 * this card just reads + claims. If the player has no active quest yet
 * (right after a reset), the card renders a "Quest wird geladen…" state
 * which auto-resolves on the next App-level tick.
 */

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function DailyQuestPanel() {
  const player = useGameStore((s) => s.player);
  const claimDailyQuest = useGameStore((s) => s.claimDailyQuest);

  // Tick the countdown once per second. State increment forces re-render
  // without subscribing the whole component to anything that would change
  // more often than the visible counter resolution.
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!player || !player.dailyQuest) {
    return (
      <section className="daily-quest card">
        <div className="daily-quest__header">
          <Icon name="forward" size={18} />
          <span className="daily-quest__title">Tagesquest</span>
        </div>
        <div className="daily-quest__loading">Quest wird geladen…</div>
      </section>
    );
  }

  const dq = player.dailyQuest;
  const def = getDailyQuest(dq.id);
  if (!def) return null;

  const pct = Math.min(100, (dq.progress / def.targetProgress) * 100);
  const complete = dq.progress >= def.targetProgress;
  const claimed = dq.claimedAt > 0;
  const countdown = formatCountdown(dq.resetAt - Date.now());
  const iconName = (def.iconKey as IconName) || 'forward';

  return (
    <section className="daily-quest card">
      <div className="daily-quest__header">
        <Icon name={iconName} size={18} />
        <span className="daily-quest__title">Tagesquest</span>
        <span className="daily-quest__reset">Reset in {countdown}</span>
      </div>
      <div className={`daily-quest__row ${claimed ? 'daily-quest__row--claimed' : ''}`}>
        <div className="daily-quest__body">
          <div className="daily-quest__name">{def.id}</div>
          <div className="daily-quest__bar">
            <div className="daily-quest__bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="daily-quest__meta">
            {dq.progress.toLocaleString('de-DE')} / {def.targetProgress.toLocaleString('de-DE')}
            {' · Belohnung: '}
            <strong>{def.reward.corruptedData} ⌬</strong>
            {' + '}
            <strong>{def.reward.ascensionFragments} ✦</strong>
          </div>
        </div>
        {claimed ? (
          <div className="daily-quest__claimed-tag">✓ Abgeholt</div>
        ) : (
          <button
            type="button"
            className="daily-quest__claim"
            onClick={() => claimDailyQuest()}
            disabled={!complete}
            title={complete ? 'Belohnung abholen' : 'Quest noch nicht abgeschlossen'}
          >
            {complete ? 'Abholen' : 'Aktiv'}
          </button>
        )}
      </div>
    </section>
  );
}
